import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { createIdentityUser } from "../actions/userActions";
import { Form, Input, Button, Typography, Space, Select, Alert, Divider, Card } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, IdcardOutlined } from '@ant-design/icons';
import { IDENTITY_USER_CREATE_RESET } from "../constants/userConstants";
import { listBusinessOrganisations } from "../actions/businessOrganisationActions";
import MicrosoftOAuth from '../components/auth/MicrosoftOAuth.component';
import Logo from '../components/logo/Logo.Component';

const { Text, Link: TypeLink, Title } = Typography;

function RegisterScreen() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();

    const [form] = Form.useForm();
    const [newIdentityUser, setNewIdentityUser] = useState({});

    const redirect = location.search ? location.search.split("=")[1] : "/";

    const identityUserCreate = useSelector((state) => state.identityUserCreate);
    const { loading, error, success } = identityUserCreate;

    useEffect(() => {
        dispatch({ type: IDENTITY_USER_CREATE_RESET });
        dispatch(listBusinessOrganisations());
    }, [dispatch]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                dispatch({ type: IDENTITY_USER_CREATE_RESET });
                window.location.href = '/userlogin';
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [success, dispatch]);

    const onFinish = (values) => {
        dispatch(createIdentityUser(values));
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <Logo mode="text" />
                </div>
                <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 32 }}>
                    <Title level={3} style={{ textAlign: 'center', marginBottom: 24 }}>
                        {t('register.title')}
                    </Title>
                    {error && (
                        <Alert
                            message={t("Error")}
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    {success && (
                        <Alert
                            message={t("Success")}
                            description={t('register.successMessage')}
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    <div style={{ width: '100%', marginTop: 16 }}>
                        <MicrosoftOAuth />
                    </div>
                    <Divider plain>{t('Or continue with email')}</Divider>
                    <Form
                        form={form}
                        name="register"
                        onFinish={onFinish}
                        layout="vertical"
                        size="large"
                    >
                        <Form.Item
                            name="businessOrganisationId"
                            rules={[{ required: true, message: t('register.organisationRequired') }]}
                        >
                            <Select
                                placeholder={t('register.selectOrganisation')}
                                onChange={(value) => setNewIdentityUser({ ...newIdentityUser, businessOrganisationId: value })}
                            >
                                <Select.Option value="4fd0a57b-2789-4e43-a6af-9c263f258a6f">Mosaic Manager</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="email"
                            rules={[
                                { required: true, message: t('register.emailRequired') },
                                { type: 'email', message: t('register.invalidEmail') }
                            ]}
                        >
                            <Input 
                                prefix={<MailOutlined />} 
                                placeholder={t('register.emailPlaceholder')}
                                onChange={(e) => setNewIdentityUser({ ...newIdentityUser, email: e.target.value })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="userName"
                            rules={[{ required: true, message: t('register.usernameRequired') }]}
                        >
                            <Input 
                                prefix={<UserOutlined />} 
                                placeholder={t('register.usernamePlaceholder')}
                                onChange={(e) => setNewIdentityUser({ ...newIdentityUser, userName: e.target.value })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="firstName"
                            rules={[{ required: true, message: t('register.firstNameRequired') }]}
                        >
                            <Input 
                                prefix={<IdcardOutlined />} 
                                placeholder={t('register.firstNamePlaceholder')}
                                onChange={(e) => setNewIdentityUser({ ...newIdentityUser, firstName: e.target.value })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="lastName"
                            rules={[{ required: true, message: t('register.lastNameRequired') }]}
                        >
                            <Input 
                                prefix={<IdcardOutlined />} 
                                placeholder={t('register.lastNamePlaceholder')}
                                onChange={(e) => setNewIdentityUser({ ...newIdentityUser, lastName: e.target.value })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: t('register.passwordRequired') }]}
                        >
                            <Input.Password 
                                prefix={<LockOutlined />} 
                                placeholder={t('register.passwordPlaceholder')}
                                onChange={(e) => setNewIdentityUser({ ...newIdentityUser, password: e.target.value })}
                            />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={loading}>
                                {t('register.registerButton')}
                            </Button>
                        </Form.Item>
                    </Form>
                    <Text type="secondary" style={{ textAlign: 'center', display: 'block', marginTop: 16 }}>
                        {t('register.alreadyHaveAccount')} <Link to={redirect ? `/userlogin?redirect=${redirect}` : "/userlogin"}>{t('register.signInLink')}</Link>
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px', textAlign: 'center', display: 'block', marginTop: 16 }}>
                        By continuing, you agree to our{' '}
                        <TypeLink href="https://aro.tech/terms">Terms and Conditions</TypeLink> and{' '}
                        <TypeLink href="https://aro.tech/privacy-policy">Privacy Policy</TypeLink>
                    </Text>
                </Card>
            </Space>
    );
}

export default RegisterScreen;